import type { AudioManager } from './audio';

export interface AudioSettingsSnapshot {
  readonly masterVolume: number;
  readonly musicVolume: number;
  readonly sfxVolume: number;
  readonly musicMuted: boolean;
  readonly sfxMuted: boolean;
}

export type AudioSettingsPatch = Partial<AudioSettingsSnapshot>;

export interface AudioSettingsController {
  getSnapshot(): AudioSettingsSnapshot;
  setSnapshot(patch: AudioSettingsPatch): AudioSettingsSnapshot;
  applyCurrent(): void;
}

export type AudioSettingsAppliedHandler = (
  next: AudioSettingsSnapshot,
  previous: AudioSettingsSnapshot,
) => void;

const DEFAULT_AUDIO_SETTINGS: AudioSettingsSnapshot = {
  masterVolume: 0.7,
  musicVolume: 0.7,
  sfxVolume: 0.7,
  musicMuted: false,
  sfxMuted: false,
};

const CANONICAL_KEYS = {
  masterVolume: 'smashparty.audio.masterVolume',
  musicVolume: 'smashparty.audio.musicVolume',
  sfxVolume: 'smashparty.audio.sfxVolume',
  musicMuted: 'smashparty.audio.musicMuted',
  sfxMuted: 'smashparty.audio.sfxMuted',
} as const;

const LEGACY_UI_VOLUME_KEYS = {
  masterVolume: 'smashparty_settings.audio.master',
  musicVolume: 'smashparty_settings.audio.music',
  sfxVolume: 'smashparty_settings.audio.sfx',
} as const;

const LEGACY_MANAGER_VOLUME_KEYS = {
  masterVolume: 'smashparty.audio.volume.master',
  musicVolume: 'smashparty.audio.volume.music',
  sfxVolume: 'smashparty.audio.volume.sfx',
} as const;

type VolumeSettingKey = 'masterVolume' | 'musicVolume' | 'sfxVolume';

function storage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

function parseUnitVolume(raw: string | null): number | null {
  if (raw == null) return null;
  const parsed = Number.parseFloat(raw);
  if (!Number.isFinite(parsed)) return null;
  return clamp01(parsed);
}

function parsePercentVolume(raw: string | null): number | null {
  if (raw == null) return null;
  const parsed = Number.parseFloat(raw);
  if (!Number.isFinite(parsed)) return null;
  return clamp01(parsed / 100);
}

function parseBoolean(raw: string | null): boolean | null {
  if (raw == null) return null;
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return null;
}

function readVolume(store: Pick<Storage, 'getItem'> | null, key: VolumeSettingKey): number {
  if (store == null) return DEFAULT_AUDIO_SETTINGS[key];
  return (
    parseUnitVolume(store.getItem(CANONICAL_KEYS[key])) ??
    parsePercentVolume(store.getItem(LEGACY_UI_VOLUME_KEYS[key])) ??
    parseUnitVolume(store.getItem(LEGACY_MANAGER_VOLUME_KEYS[key])) ??
    DEFAULT_AUDIO_SETTINGS[key]
  );
}

export function readAudioSettingsSnapshot(): AudioSettingsSnapshot {
  const store = storage();
  return {
    masterVolume: readVolume(store, 'masterVolume'),
    musicVolume: readVolume(store, 'musicVolume'),
    sfxVolume: readVolume(store, 'sfxVolume'),
    musicMuted:
      parseBoolean(store?.getItem(CANONICAL_KEYS.musicMuted) ?? null) ??
      DEFAULT_AUDIO_SETTINGS.musicMuted,
    sfxMuted:
      parseBoolean(store?.getItem(CANONICAL_KEYS.sfxMuted) ?? null) ??
      DEFAULT_AUDIO_SETTINGS.sfxMuted,
  };
}

export function isMusicMuted(): boolean {
  return readAudioSettingsSnapshot().musicMuted;
}

export function isSfxMuted(): boolean {
  return readAudioSettingsSnapshot().sfxMuted;
}

export function writeAudioSettingsSnapshot(snapshot: AudioSettingsSnapshot): void {
  const store = storage();
  if (store == null) return;

  const masterVolume = clamp01(snapshot.masterVolume);
  const musicVolume = clamp01(snapshot.musicVolume);
  const sfxVolume = clamp01(snapshot.sfxVolume);

  try {
    store.setItem(CANONICAL_KEYS.masterVolume, String(masterVolume));
    store.setItem(CANONICAL_KEYS.musicVolume, String(musicVolume));
    store.setItem(CANONICAL_KEYS.sfxVolume, String(sfxVolume));
    store.setItem(CANONICAL_KEYS.musicMuted, String(snapshot.musicMuted));
    store.setItem(CANONICAL_KEYS.sfxMuted, String(snapshot.sfxMuted));

    store.setItem(LEGACY_UI_VOLUME_KEYS.masterVolume, String(Math.round(masterVolume * 100)));
    store.setItem(LEGACY_UI_VOLUME_KEYS.musicVolume, String(Math.round(musicVolume * 100)));
    store.setItem(LEGACY_UI_VOLUME_KEYS.sfxVolume, String(Math.round(sfxVolume * 100)));

    store.setItem(LEGACY_MANAGER_VOLUME_KEYS.masterVolume, String(masterVolume));
    store.setItem(LEGACY_MANAGER_VOLUME_KEYS.musicVolume, String(musicVolume));
    store.setItem(LEGACY_MANAGER_VOLUME_KEYS.sfxVolume, String(sfxVolume));
  } catch {
    // Best-effort only: private browsing can reject localStorage writes.
  }
}

export function applyAudioSettingsSnapshot(
  manager: Pick<AudioManager, 'setVolume'> | null,
  snapshot: AudioSettingsSnapshot,
): void {
  if (manager == null) return;
  manager.setVolume('master', clamp01(snapshot.masterVolume));
  manager.setVolume('music', snapshot.musicMuted ? 0 : clamp01(snapshot.musicVolume));
  manager.setVolume('sfx', snapshot.sfxMuted ? 0 : clamp01(snapshot.sfxVolume));
}

export function createAudioSettingsController(opts: {
  readonly getAudioManager: () => Pick<AudioManager, 'setVolume'> | null;
  readonly onSnapshotApplied?: AudioSettingsAppliedHandler;
}): AudioSettingsController {
  writeAudioSettingsSnapshot(readAudioSettingsSnapshot());

  function getSnapshot(): AudioSettingsSnapshot {
    return readAudioSettingsSnapshot();
  }

  function applyCurrent(): void {
    applyAudioSettingsSnapshot(opts.getAudioManager(), getSnapshot());
  }

  function setSnapshot(patch: AudioSettingsPatch): AudioSettingsSnapshot {
    const previous = getSnapshot();
    const next: AudioSettingsSnapshot = {
      ...previous,
      ...patch,
    };
    writeAudioSettingsSnapshot(next);
    applyAudioSettingsSnapshot(opts.getAudioManager(), next);
    opts.onSnapshotApplied?.(next, previous);
    return next;
  }

  return {
    getSnapshot,
    setSnapshot,
    applyCurrent,
  };
}
